import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { Staff, StaffStore } from '../types';

// Initial staffs list (empty array)
const initialStaffsList: Staff[] = [];

// Staff Store
export const useStaffStore = create<StaffStore>()(
  devtools(
    (set, get) => ({
      staffs: initialStaffsList,
      staffsByDepartment: {},
      loading: false,
      error: null,

      setStaffs: (staffs: Staff[]) => set({ staffs, error: null }, false, 'setStaffs'),

      setStaffsByDepartment: (departmentId: string, staffs: Staff[], roleKey?: string) =>
        set(
          (state) => ({
            staffsByDepartment: {
              ...state.staffsByDepartment,
              [departmentId]: {
                ...state.staffsByDepartment[departmentId],
                [roleKey || 'default']: staffs,
              },
            },
            error: null,
          }),
          false,
          'setStaffsByDepartment'
        ),

      getStaffsByDepartment: (departmentId: string, roleKey?: string) => {
        const { staffsByDepartment } = get();
        const departmentData = staffsByDepartment[departmentId];
        if (!departmentData) return [];

        if (roleKey) {
          return departmentData[roleKey as keyof typeof departmentData] || [];
        }

        // For backward compatibility, check if it's old format (array) or new format (object)
        if (Array.isArray(departmentData)) {
          return departmentData;
        }

        // New format: return all staffs from all roles
        const allStaffs: Staff[] = [];
        for (const staffs of Object.values(departmentData)) {
          if (Array.isArray(staffs)) {
            allStaffs.push(...staffs);
          }
        }
        return allStaffs;
      },

      setLoading: (loading: boolean) => set({ loading }, false, 'setLoading'),

      setError: (error: string | null) => set({ error, loading: false }, false, 'setError'),

      getStaffById: (id: string) => {
        const { staffs } = get();
        return staffs.find((staff) => staff.id === id);
      },

      getStaffByCode: (code: number) => {
        const { staffs } = get();
        return staffs.find((staff) => staff.code === code);
      },

      clearStaffs: () =>
        set(
          {
            staffs: initialStaffsList,
            error: null,
            loading: false,
          },
          false,
          'clearStaffs'
        ),

      clearStaffsByDepartment: () =>
        set(
          {
            staffsByDepartment: {},
            error: null,
            loading: false,
          },
          false,
          'clearStaffsByDepartment'
        ),
    }),
    {
      name: 'staff-store',
    }
  )
);
